/**
 * CrewAssignmentComposer — Supervisor's 4-step wizard for building a
 * Crew Assignment card that gets dispatched to a Crew Leader.
 *
 * Flow (locked stepper, each step gated by the previous one):
 *   Step 1 — Pick ONE Crew Leader from the region's roster
 *   Step 2 — Pick the Cities in scope for this run
 *   Step 3 — Pick 4-6 Site Agents that will report to this Crew Leader
 *   Step 4 — Pick the Properties (filtered by chosen cities); review + Dispatch
 *
 * On submit → POST /api/ops/crew-assignments; toast + reload the card list.
 */
import { useMemo, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle2, MapPin, Users, HardHat, Building2, Lock, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_LABEL = ['Crew Leader', 'Cities', 'Site Agents', 'Properties'];

export default function CrewAssignmentComposer({
  region,
  crewLeaders = [],
  agents = [],
  properties = [],
  onCreated,
}) {
  const [step, setStep] = useState(1);
  const [crewLeaderId, setCrewLeaderId] = useState('');
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [selectedPropIds, setSelectedPropIds] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const cities = useMemo(
    () => [...new Set(properties.map((p) => (p.city || '').trim()).filter(Boolean))].sort(),
    [properties],
  );

  const visibleProperties = useMemo(() => {
    if (selectedCities.length === 0) return properties;
    return properties.filter((p) => selectedCities.includes((p.city || '').trim()));
  }, [properties, selectedCities]);

  const totalUnits = useMemo(
    () => visibleProperties
      .filter((p) => selectedPropIds.includes(p.property_id))
      .reduce((sum, p) => sum + (Number(p.total_units) || 0), 0),
    [visibleProperties, selectedPropIds],
  );

  const chosenLeader = crewLeaders.find((c) => c.user_id === crewLeaderId);

  const reset = () => {
    setStep(1);
    setCrewLeaderId('');
    setSelectedCities([]);
    setSelectedAgentIds([]);
    setSelectedPropIds([]);
    setNotes('');
  };

  const submit = async () => {
    if (selectedAgentIds.length < 1 || selectedAgentIds.length > 8) {
      toast.error('Pick between 1 and 8 site agents (4-6 recommended).');
      return;
    }
    if (selectedPropIds.length === 0) {
      toast.error('Pick at least one property.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/ops/crew-assignments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          crew_leader_id: crewLeaderId,
          cities: selectedCities,
          agent_ids: selectedAgentIds,
          property_ids: selectedPropIds,
          notes: notes || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Failed to dispatch assignment');
        return;
      }
      toast.success(`Crew assignment dispatched to ${chosenLeader?.name || 'Crew Leader'}.`);
      reset();
      onCreated?.(data.assignment);
    } catch (_) {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  const StepDot = ({ n, label, complete, active, disabled }) => (
    <div className="flex items-center gap-1.5">
      <span
        className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
          complete
            ? 'bg-emerald-500 text-black'
            : active
              ? 'bg-[#C9A962] text-black'
              : disabled
                ? 'bg-zinc-800 text-zinc-500'
                : 'bg-zinc-700 text-zinc-300'
        }`}
        data-testid={`crew-composer-step-dot-${n}`}
      >
        {complete ? <CheckCircle2 className="h-3.5 w-3.5" /> : n}
      </span>
      <span className={`text-[11px] font-bold uppercase tracking-widest ${active ? 'text-[#C9A962]' : disabled ? 'text-zinc-600' : 'text-zinc-400'}`}>
        {label}
      </span>
    </div>
  );

  return (
    <Card
      className="p-5 border"
      style={{ backgroundColor: '#0b0b0b', borderColor: '#C9A962' }}
      data-testid="crew-assignment-composer"
    >
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h3
            className="text-lg font-bold"
            style={{
              fontFamily: 'Barlow Condensed, sans-serif',
              color: '#C9A962',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            New Crew Assignment
          </h3>
          <p className="text-[11px] text-zinc-400 mt-0.5">
            {region ? `${region.region_code || ''} ${region.name || ''}` : 'Your region'} — build a job card for one Crew Leader at a time.
          </p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          {STEP_LABEL.map((label, i) => (
            <StepDot
              key={label}
              n={i + 1}
              label={label}
              complete={step > i + 1}
              active={step === i + 1}
              disabled={step < i + 1}
            />
          ))}
        </div>
      </div>

      {/* STEP 1 — Crew Leader */}
      <section className="mb-3" data-testid="crew-composer-step-1">
        <p className="text-[11px] uppercase tracking-widest font-bold text-zinc-300 mb-2 flex items-center gap-1">
          <HardHat className="h-3.5 w-3.5 text-[#C9A962]" /> Step 1 · Pick a Crew Leader
        </p>
        <div className="flex flex-wrap gap-2">
          {crewLeaders.length === 0 ? (
            <p className="text-xs text-zinc-500">No Crew Leaders in your region.</p>
          ) : crewLeaders.map((cl) => {
            const active = crewLeaderId === cl.user_id;
            return (
              <button
                key={cl.user_id}
                type="button"
                onClick={() => {
                  setCrewLeaderId(cl.user_id);
                  setStep(Math.max(step, 2));
                }}
                className={`px-3 py-1.5 rounded-md text-xs font-bold border transition ${
                  active
                    ? 'bg-[#C9A962] border-[#C9A962] text-black'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-200 hover:border-[#C9A962]'
                }`}
                data-testid={`crew-composer-leader-${cl.user_id}`}
              >
                {cl.name} {cl.badge_number && <span className="text-[10px] opacity-70">· {cl.badge_number}</span>}
              </button>
            );
          })}
        </div>
      </section>

      {/* STEP 2 — Cities (locked until crew leader picked) */}
      <section
        className={`mb-3 ${!crewLeaderId ? 'opacity-40 pointer-events-none' : ''}`}
        data-testid="crew-composer-step-2"
      >
        <p className="text-[11px] uppercase tracking-widest font-bold text-zinc-300 mb-2 flex items-center gap-1">
          <MapPin className="h-3.5 w-3.5 text-[#C9A962]" /> Step 2 · Cities in scope ({selectedCities.length || 'all'})
          {!crewLeaderId && <Lock className="h-3 w-3 text-zinc-500 ml-1" />}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {cities.map((c) => {
            const active = selectedCities.includes(c);
            return (
              <button
                key={c}
                type="button"
                onClick={() => {
                  setSelectedCities((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
                  setStep(Math.max(step, 3));
                }}
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition ${
                  active
                    ? 'bg-blue-600 border-blue-500 text-white'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-blue-500'
                }`}
                data-testid={`crew-composer-city-${c.replace(/\s+/g, '-')}`}
              >
                {c}
              </button>
            );
          })}
        </div>
      </section>

      {/* STEP 3 — Site Agents (locked until at least one city) */}
      <section
        className={`mb-3 ${selectedCities.length === 0 ? 'opacity-40 pointer-events-none' : ''}`}
        data-testid="crew-composer-step-3"
      >
        <p className="text-[11px] uppercase tracking-widest font-bold text-zinc-300 mb-2 flex items-center gap-1">
          <Users className="h-3.5 w-3.5 text-[#C9A962]" /> Step 3 · Site Agents ({selectedAgentIds.length} · 4-6 recommended)
          {selectedCities.length === 0 && <Lock className="h-3 w-3 text-zinc-500 ml-1" />}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {agents.length === 0 ? (
            <p className="text-xs text-zinc-500">No Site Agents in your region.</p>
          ) : agents.map((a) => {
            const active = selectedAgentIds.includes(a.user_id);
            return (
              <button
                key={a.user_id}
                type="button"
                onClick={() => {
                  setSelectedAgentIds((prev) => {
                    const next = prev.includes(a.user_id) ? prev.filter((x) => x !== a.user_id) : [...prev, a.user_id];
                    if (next.length && step < 4) setStep(4);
                    return next;
                  });
                }}
                className={`px-2.5 py-1 rounded-md text-[11px] font-bold border transition ${
                  active
                    ? 'bg-emerald-600 border-emerald-500 text-white'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-emerald-500'
                }`}
                data-testid={`crew-composer-agent-${a.user_id}`}
              >
                {a.name} {a.badge_number && <span className="text-[10px] opacity-70">· {a.badge_number}</span>}
              </button>
            );
          })}
        </div>
      </section>

      {/* STEP 4 — Properties (locked until at least one agent picked) */}
      <section
        className={`mb-3 ${selectedAgentIds.length === 0 ? 'opacity-40 pointer-events-none' : ''}`}
        data-testid="crew-composer-step-4"
      >
        <p className="text-[11px] uppercase tracking-widest font-bold text-zinc-300 mb-2 flex items-center gap-1">
          <Building2 className="h-3.5 w-3.5 text-[#C9A962]" /> Step 4 · Properties ({selectedPropIds.length} · {totalUnits} units total)
          {selectedAgentIds.length === 0 && <Lock className="h-3 w-3 text-zinc-500 ml-1" />}
        </p>
        <div className="space-y-1 max-h-56 overflow-y-auto pr-1" data-testid="crew-composer-property-list">
          {visibleProperties.length === 0 ? (
            <p className="text-xs text-zinc-500">No properties match the selected cities.</p>
          ) : visibleProperties.map((p) => {
            const active = selectedPropIds.includes(p.property_id);
            return (
              <button
                key={p.property_id}
                type="button"
                onClick={() => setSelectedPropIds((prev) => (prev.includes(p.property_id) ? prev.filter((x) => x !== p.property_id) : [...prev, p.property_id]))}
                className={`w-full text-left px-2.5 py-1.5 rounded-md text-xs border transition flex items-center justify-between gap-2 ${
                  active
                    ? 'bg-blue-600/20 border-blue-500 text-white'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-blue-500'
                }`}
                data-testid={`crew-composer-property-${p.property_id}`}
              >
                <span className="min-w-0">
                  <span className="font-bold truncate block">{p.property_name}</span>
                  <span className="text-[10px] text-zinc-500">{p.city || '—'}</span>
                </span>
                <Badge className="bg-zinc-700 text-zinc-200 text-[10px] flex-shrink-0">
                  {p.total_units ?? '—'} units
                </Badge>
              </button>
            );
          })}
        </div>
      </section>

      {/* Notes + Dispatch */}
      {selectedPropIds.length > 0 && (
        <div className="pt-2 border-t border-zinc-800 flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional note for the crew leader (route order, contact, etc.)"
            className="flex-1 min-w-[220px] bg-zinc-900 border border-zinc-700 rounded-md px-3 py-1.5 text-xs text-white"
            data-testid="crew-composer-notes"
          />
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-[#C9A962] hover:bg-[#b89651] text-black font-bold"
            data-testid="crew-composer-dispatch-btn"
          >
            <Send className="h-4 w-4 mr-1" />
            {saving ? 'Dispatching…' : 'Dispatch Assignment'}
          </Button>
        </div>
      )}
    </Card>
  );
}
